/**
 * useActuators - React hook pro spravu aktuatoru
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { actuatorService } from '../services/actuatorService';
import { scheduleService } from '../services/scheduleService';
import { apiClient } from '../services/api';
import { Actuator, ActuatorCategory, TestModeState } from '../types/actuators';
import { Device, DeviceResponse } from '../types/system';
import { useWebSocket } from './useWebSocket';
import { useUserPreferences } from './useUserPreferences';

interface UseActuatorsOptions {
  deviceId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseActuatorsReturn {
  actuators: Actuator[];
  favorites: Actuator[];
  activeActuators: Actuator[];
  isLoading: boolean;
  error: Error | null;
  // Akce
  turnOn: (actuatorId: string) => Promise<void>;
  turnOff: (actuatorId: string) => Promise<void>;
  toggle: (actuatorId: string) => Promise<void>;
  setValue: (actuatorId: string, value: number) => Promise<void>;
  runTimed: (actuatorId: string, value: number, durationSec: number) => Promise<void>;
  emergencyStop: () => Promise<void>;
  resetEmergency: () => Promise<void>;
  isEmergencyStopped: boolean;
  toggleFavorite: (actuatorId: string) => Promise<void>;
  // Test mode
  testModeState: TestModeState | null;
  startTestMode: () => Promise<TestModeState | null>;
  stopTestMode: () => Promise<void>;
  clearTestMode: () => void;
  // Filtry
  filterByCategory: (category: ActuatorCategory | 'all') => Actuator[];
  // Refetch
  refetch: () => void;
}

export const useActuators = ({
  deviceId,
  autoRefresh = true,
  refreshInterval = 5000,
}: UseActuatorsOptions): UseActuatorsReturn => {
  const queryClient = useQueryClient();
  const [testModeState, setTestModeState] = useState<TestModeState | null>(null);
  const testModeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevConnectedRef = useRef(false);

  // Query pro aktuatory - pouze pokud mame deviceId
  const {
    data: actuators = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Actuator[]>({
    queryKey: ['actuators', deviceId],
    queryFn: async () => {
      const [actuatorSettled, scheduleSettled] = await Promise.allSettled([
        actuatorService.getActuators(deviceId),
        scheduleService.getSchedules({ deviceId, limit: 100 }),
      ]);

      if (actuatorSettled.status === 'rejected') {
        console.error('Failed to fetch actuators:', actuatorSettled.reason);
        throw actuatorSettled.reason;
      }

      const actuatorsData = actuatorSettled.value as Actuator[];
      const schedules = scheduleSettled.status === 'fulfilled' ? scheduleSettled.value.data : [];

      if (scheduleSettled.status === 'rejected') {
        console.warn(
          'Failed to fetch schedules, continuing without schedule data:',
          scheduleSettled.reason,
        );
      }

      return actuatorsData.map((actuator) => {
        // Najit nejblizsi rozvrh pro tento aktuator
        // Predpokladame ze taskParameters obsahuje actuatorId
        const actuatorSchedules = schedules.filter(
          (s) => s.taskParameters?.['actuatorId'] === actuator.id && s.enabled && s.nextExecution,
        );

        // Seradit podle casu a vybrat nejblizsi
        const nextExec =
          actuatorSchedules.length > 0
            ? actuatorSchedules.sort(
                (a, b) => (a.nextExecution?.getTime() || 0) - (b.nextExecution?.getTime() || 0),
              )[0].nextExecution
            : undefined;

        return {
          ...actuator,
          nextExecution: nextExec,
        };
      });
    },
    refetchInterval: autoRefresh && !!deviceId ? refreshInterval : false,
    staleTime: 5000,
    enabled: !!deviceId, // Nevolat pokud neni deviceId
  });

  // WebSocket real-time updates
  const { on, off, subscribe, unsubscribe, isConnected } = useWebSocket();

  useEffect(() => {
    if (!deviceId) return;

    // Prilasit se k odberu
    subscribe('actuators', deviceId);

    const handleActuatorUpdate = (message: any) => {
      if (message.type === 'actuator_update' && message.data.deviceId === deviceId) {
        const { actuatorId, relayIndex, state, value } = message.data;

        queryClient.setQueryData(['actuators', deviceId], (oldData: Actuator[] | undefined) => {
          if (!oldData) return [];
          return oldData.map((a) => {
            const matches =
              (typeof relayIndex === 'number' && a.relayIndex === relayIndex) ||
              a.id.toLowerCase() === String(actuatorId).toLowerCase();
            return matches
              ? { ...a, state, value: value ?? a.value, lastStateChange: new Date() }
              : a;
          });
        });
      }
    };

    on('actuator_update', handleActuatorUpdate);

    const handleScheduleUpdate = (message: any) => {
      if (message.type === 'schedule_update' && message.data.deviceId === deviceId) {
        // Refresh actuators + schedules so nextExecution is recalculated
        queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
      }
    };

    on('schedule_update', handleScheduleUpdate);

    return () => {
      unsubscribe('actuators', deviceId);
      off('actuator_update', handleActuatorUpdate);
      off('schedule_update', handleScheduleUpdate);
    };
  }, [deviceId, on, off, subscribe, unsubscribe, queryClient]);

  // Resync actuator state after WebSocket reconnect
  useEffect(() => {
    if (isConnected && !prevConnectedRef.current && deviceId) {
      refetch();
      subscribe('actuators', deviceId);
    }
    prevConnectedRef.current = isConnected;
  }, [isConnected, deviceId, refetch, subscribe]);

  /**
   * Selhani prikazu na hardware.
   *
   * Mutace nize prepinaji stav optimisticky (onMutate), aby UI reagovalo okamzite.
   * Kdyz ale prikaz selze, musi se prepinac vratit — jinak by uzivatel videl stav,
   * ktery na zarizeni vubec nenastal, a myslel si, ze cerpadlo bezi. Drive se chyba
   * jen zalogovala do konzole a nikde se neprojevila.
   */
  const handleCommandError = useCallback(
    (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Příkaz se nepodařilo odeslat. Zkuste to znovu.',
      );
    },
    [queryClient, deviceId],
  );

  // Mutace pro prikazy
  const commandMutation = useMutation({
    mutationFn: async ({ actuatorId, state }: { actuatorId: string; state: 'ON' | 'OFF' }) => {
      return actuatorService.sendCommand({
        deviceId,
        actuatorId,
        state,
      });
    },
    onMutate: async ({ actuatorId, state }: { actuatorId: string; state: 'ON' | 'OFF' }) => {
      queryClient.setQueryData(['actuators', deviceId], (oldData: Actuator[] | undefined) => {
        if (!oldData) return [];
        return oldData.map((a) =>
          a.id === actuatorId ? { ...a, state, lastStateChange: new Date() } : a,
        );
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
    },
    onError: handleCommandError,
  });

  // Mutace pro nastaveni PWM hodnoty (svetla 0-100%)
  const setValueMutation = useMutation({
    mutationFn: async ({ actuatorId, value }: { actuatorId: string; value: number }) => {
      return actuatorService.setValue(deviceId, actuatorId, value);
    },
    onMutate: async ({ actuatorId, value }: { actuatorId: string; value: number }) => {
      // Optimisticka aktualizace - okamzite zobrazit novou hodnotu
      queryClient.setQueryData(['actuators', deviceId], (oldData: Actuator[] | undefined) => {
        if (!oldData) return [];
        return oldData.map((a) =>
          a.id === actuatorId
            ? { ...a, value, state: value > 0 ? ('ON' as const) : ('OFF' as const), lastStateChange: new Date() }
            : a,
        );
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
    },
    onError: handleCommandError,
  });

  // Mutace pro casovane spusteni aktuatoru (PWM hodnota + doba behu)
  const runTimedMutation = useMutation({
    mutationFn: async ({ actuatorId, value, durationSec }: { actuatorId: string; value: number; durationSec: number }) => {
      return actuatorService.sendCommand({
        deviceId,
        actuatorId,
        state: 'ON',
        value: Math.max(0, Math.min(100, value)),
        duration: durationSec,
      });
    },
    onMutate: async ({ actuatorId, value }: { actuatorId: string; value: number; durationSec: number }) => {
      queryClient.setQueryData(['actuators', deviceId], (oldData: Actuator[] | undefined) => {
        if (!oldData) return [];
        return oldData.map((a) =>
          a.id === actuatorId
            ? { ...a, value, state: 'ON' as const, lastStateChange: new Date() }
            : a,
        );
      });
    },
    onSuccess: (_, { actuatorId, durationSec }) => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
      // Optimistic fallback: mark actuator as OFF after duration expires.
      // Firmware publishes MQTT state OFF after the timer, which arrives via
      // WebSocket. This timeout is a safety net in case the WS update is late.
      const timeoutMs = durationSec * 1000 + 1500;
      setTimeout(() => {
        queryClient.setQueryData(['actuators', deviceId], (oldData: Actuator[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((a) =>
            a.id === actuatorId && a.state === 'ON'
              ? { ...a, state: 'OFF' as const, value: 0, lastStateChange: new Date() }
              : a,
          );
        });
        queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
      }, timeoutMs);
    },
  });

  // Mutace pro emergency stop
  const emergencyStopMutation = useMutation({
    mutationFn: () => actuatorService.emergencyStop(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
    },
    onError: handleCommandError,
  });

  // Mutace pro zruseni emergency stop
  const resetEmergencyMutation = useMutation({
    mutationFn: () => actuatorService.resetEmergencyStop(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
    },
    onError: handleCommandError,
  });

  // Mutace pro favorite
  const favoriteMutation = useMutation({
    mutationFn: (actuatorId: string) => actuatorService.toggleFavorite(deviceId, actuatorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actuators', deviceId] });
    },
    onError: handleCommandError,
  });

  // Akce
  const turnOn = useCallback(
    async (actuatorId: string) => {
      await commandMutation.mutateAsync({ actuatorId, state: 'ON' });
    },
    [commandMutation],
  );

  const turnOff = useCallback(
    async (actuatorId: string) => {
      await commandMutation.mutateAsync({ actuatorId, state: 'OFF' });
    },
    [commandMutation],
  );

  const toggle = useCallback(
    async (actuatorId: string) => {
      const actuator = actuators.find((a) => a.id === actuatorId);
      if (actuator) {
        const newState = actuator.state === 'ON' ? 'OFF' : 'ON';
        await commandMutation.mutateAsync({ actuatorId, state: newState });
      }
    },
    [actuators, commandMutation],
  );

  const setValue = useCallback(
    async (actuatorId: string, value: number) => {
      await setValueMutation.mutateAsync({ actuatorId, value });
    },
    [setValueMutation],
  );

  const runTimed = useCallback(
    async (actuatorId: string, value: number, durationSec: number) => {
      await runTimedMutation.mutateAsync({ actuatorId, value, durationSec });
    },
    [runTimedMutation],
  );

  const emergencyStop = useCallback(async () => {
    await emergencyStopMutation.mutateAsync();
  }, [emergencyStopMutation]);

  const resetEmergency = useCallback(async () => {
    await resetEmergencyMutation.mutateAsync();
  }, [resetEmergencyMutation]);

  const toggleFavorite = useCallback(
    async (actuatorId: string) => {
      await favoriteMutation.mutateAsync(actuatorId);
    },
    [favoriteMutation],
  );

  // Test mode
  const startTestMode = useCallback(async (actuatorIds?: string[]) => {
    const result = await actuatorService.startTestMode(deviceId, { actuatorIds });
    if (result) {
      setTestModeState(result);
    }
    return result;
  }, [deviceId]);

  const stopTestMode = useCallback(async () => {
    const sessionId = testModeState?.sessionId;
    await actuatorService.stopTestMode(deviceId, sessionId);
    setTestModeState(null);
    if (testModeIntervalRef.current) {
      clearInterval(testModeIntervalRef.current);
      testModeIntervalRef.current = null;
    }
  }, [deviceId, testModeState?.sessionId]);

  const clearTestMode = useCallback(() => {
    setTestModeState(null);
  }, []);

  // Error polling
  useEffect(() => {
    if (!testModeState?.sessionId || testModeState.status === 'completed' || testModeState.status === 'aborted') {
      if (testModeIntervalRef.current) {
        clearInterval(testModeIntervalRef.current);
        testModeIntervalRef.current = null;
      }
      return;
    }

    if (!testModeIntervalRef.current) {
      testModeIntervalRef.current = setInterval(async () => {
        try {
          const status = await actuatorService.getTestModeStatus(deviceId, testModeState.sessionId!);
          if (status) {
            setTestModeState(status);
            if (status.status === 'completed' || status.status === 'aborted') {
              if (testModeIntervalRef.current) {
                clearInterval(testModeIntervalRef.current);
                testModeIntervalRef.current = null;
              }
            }
          }
        } catch (error) {
          console.error('Chyba pri zjistovani stavu test mode:', error);
        }
      }, 1000); // Polling every 1s
    }

    // Cleanup inside the effect
    return () => {
      // We purposefully don't clear the interval here immediately on every re-render
      // unless the session id or status changes to completed/aborted (handled above).
      // But we need a component unmount cleanup just in case.
    };
  }, [testModeState?.sessionId, testModeState?.status, deviceId]);

  // General unmount cleanup
  useEffect(() => {
    return () => {
      if (testModeIntervalRef.current) {
        clearInterval(testModeIntervalRef.current);
      }
    };
  }, []);

  // Filtry
  const filterByCategory = useCallback(
    (category: ActuatorCategory | 'all') => {
      if (category === 'all') return actuators;
      return actuators.filter((a) => a.category === category);
    },
    [actuators],
  );

  // Odvozene hodnoty
  const favorites = actuators.filter((a) => a.isFavorite);
  const activeActuators = actuators.filter((a) => a.state === 'ON');
  const isEmergencyStopped = actuators.length > 0 && actuators.some((a) => a.isEmergencyStopped);

  return {
    actuators,
    favorites,
    activeActuators,
    isLoading,
    error: error as Error | null,
    turnOn,
    turnOff,
    toggle,
    setValue,
    runTimed,
    emergencyStop,
    resetEmergency,
    isEmergencyStopped,
    toggleFavorite,
    testModeState,
    startTestMode,
    stopTestMode,
    clearTestMode,
    filterByCategory,
    refetch,
  };
};

/**
 * Hook pro zjištění aktivního zařízení uživatele.
 *
 * Zdroje pravdy (v tomto pořadí):
 *   1. `user_preferences.default_device_id` na serveru (sdílené mezi prohlížeči)
 *   2. `localStorage.activeDeviceId` — offline fallback / před prvním serverovým syncem
 *   3. první zařízení ze `/devices`, pokud nic jiného není nastaveno
 *
 * `setDevice(id)` provádí optimistický update: okamžitě změní lokální stav
 * i React Query cache `userPreferences`, a paralelně pošle PUT `/users/preferences`.
 */
export const useActiveDevice = () => {
  const queryClient = useQueryClient();
  const { preferences, isLoading: prefsLoading, updatePreferences } = useUserPreferences();

  const devicesQuery = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await apiClient.get<DeviceResponse>('/devices');
      return Array.isArray(response) ? (response as Device[]) : response.data || [];
    },
    staleTime: 1000 * 60,
  });

  const devices = devicesQuery.data || [];
  const validIds = useMemo(
    () => devices.map((d) => String(d.id || d.device_id)).filter(Boolean),
    [devices],
  );

  // Prefer the first online device, fall back to first device overall
  const preferredFallbackId = useMemo(() => {
    const onlineDevice = devices.find((d) => d.status === 'online');
    return String((onlineDevice ?? devices[0])?.id || (onlineDevice ?? devices[0])?.device_id || '') || null;
  }, [devices]);

  const [localActive, setLocalActive] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('activeDeviceId') : null,
  );

  const activeDeviceId = useMemo(() => {
    const serverId = preferences?.default_device_id;
    if (serverId && validIds.includes(String(serverId))) return String(serverId);
    // localActive may be stale (device deleted) — only use if still valid
    if (localActive && validIds.includes(localActive)) return localActive;
    return preferredFallbackId;
  }, [preferences?.default_device_id, localActive, validIds, preferredFallbackId]);

  useEffect(() => {
    if (!activeDeviceId || !validIds.length) return;
    if (localActive !== activeDeviceId) {
      setLocalActive(activeDeviceId);
      localStorage.setItem('activeDeviceId', activeDeviceId);
    }
    if (preferences && !prefsLoading) {
      const storedId = preferences.default_device_id;
      // Update preference if missing OR if stored device no longer exists
      if (!storedId || !validIds.includes(String(storedId))) {
        updatePreferences({ default_device_id: activeDeviceId });
      }
    }
  }, [activeDeviceId, preferences, prefsLoading, localActive, validIds, updatePreferences]);

  const setDevice = useCallback(
    (deviceId: string) => {
      setLocalActive(deviceId);
      localStorage.setItem('activeDeviceId', deviceId);
      queryClient.setQueryData(['userPreferences'], (old: any) =>
        old ? { ...old, default_device_id: deviceId } : old,
      );
      updatePreferences({ default_device_id: deviceId });
    },
    [queryClient, updatePreferences],
  );

  return {
    activeDeviceId,
    setDevice,
    isLoading: devicesQuery.isLoading || prefsLoading,
    devices,
  };
};
